import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../utils/apiClient';

interface FollowButtonProps {
    jurisprudenceId: string;
    initialFollowState?: boolean;
    onFollowChange?: (id: string, isFollowed: boolean) => void;
}

const FollowButton: React.FC<FollowButtonProps> = ({
    jurisprudenceId,
    initialFollowState,
    onFollowChange
}) => {
    const [isFollowed, setIsFollowed] = useState(initialFollowState ?? false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(initialFollowState !== undefined);

    useEffect(() => {
        if (initialFollowState !== undefined) {
            setIsFollowed(initialFollowState);
            setIsInitialized(true);
            return;
        }

        const checkFollowStatus = async () => {
            if (!jurisprudenceId) return;

            try {
                const response = await apiClient('/jurisprudence-suivi/');

                if (response.ok) {
                    const data = await response.json();

                    let suivis = [];
                    if (Array.isArray(data)) {
                        suivis = data;
                    } else if (data.suivis && Array.isArray(data.suivis)) {
                        suivis = data.suivis;
                    } else {
                        console.warn('Structure de réponse inattendue pour /jurisprudence-suivi/:', data);
                        return;
                    }

                    const isCurrentlyFollowed = suivis.some((suivi: any) => suivi.jurisprudenceId === jurisprudenceId);
                    setIsFollowed(isCurrentlyFollowed);
                } else {
                    console.error('Erreur API jurisprudence-suivi:', response.status, response.statusText);
                }
            } catch (error) {
                console.error('Erreur lors de la vérification du statut de suivi:', error);
            } finally {
                setIsInitialized(true);
            }
        };

        checkFollowStatus();
    }, [jurisprudenceId, initialFollowState]);

    const handleToggleFollow = async () => {
        if (isLoading) return;

        setIsLoading(true);

        try {
            if (isFollowed) {
                // Désactiver le suivi (DELETE)

                const response = await apiClient(`/jurisprudence-suivi/${jurisprudenceId}`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Erreur lors de la suppression du suivi');
                }

                setIsFollowed(false);

                // Notifier le parent
                if (onFollowChange) {
                    onFollowChange(jurisprudenceId, false);
                }

            } else {
                // Activer le suivi (POST)

                const response = await apiClient('/jurisprudence-suivi/', {
                    method: 'POST',
                    body: JSON.stringify({
                        jurisprudenceId: jurisprudenceId,
                        alertFrequency: 'daily'
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Erreur lors de l\'ajout du suivi');
                }

                setIsFollowed(true);

                if (onFollowChange) {
                    onFollowChange(jurisprudenceId, true);
                }
            }
        } catch (error) {
            console.error('Erreur lors du suivi/désuivi de la jurisprudence:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isInitialized) {
        return (
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 animate-pulse">
                <div className="w-3 h-3 border border-gray-300 dark:border-gray-600 rounded-full"></div>
                Vérification...
            </div>
        );
    }

    return (
        <button
            onClick={handleToggleFollow}
            disabled={isLoading}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${isFollowed
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800'
                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
        >
            {isLoading ? (
                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isFollowed ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    )}
                </svg>
            )}
            {isLoading ? 'Chargement...' : (isFollowed ? 'Suivi' : 'Suivre')}
        </button>
    );
};

export default FollowButton;